import { isAuthenticated } from '@/Config/auth/authStore';

/**
 * Why an article failed to load, and whether trying again can change the answer.
 *
 * A user met this panel in production and clicked "Try again" twelve times over 2m19s. Every
 * click reached the backend, which found the article and still did not return it. The retry
 * re-issues a byte-identical request -- same URL, and the same `scenario-id` header, because
 * `getScenarioId()` re-derives it from `window.location.pathname` -- so a settled 4xx answers
 * identically forever.
 */
export type ArticleLoadFailureReason =
  | 'not_found'
  | 'session_expired'
  | 'auth_refresh_failed'
  | 'forbidden'
  | 'bad_request'
  | 'backend_error'
  | 'network_error';

export const articleLoadFailureStatus = (error: unknown): number | null =>
  (error as { response?: { status?: number } } | null)?.response?.status ?? null;

/**
 * A bounded dimension, so raw error text never reaches analytics (the convention
 * `saveFailureReason` follows in ConversationBadgeSettings.dialog.tsx). The exact status is
 * reported alongside it rather than folded into it: the incident above narrows only to "a 4xx
 * raised after the article was found", and no bucket safe enough to be a dimension can name it.
 *
 * `signedIn` splits the two very different things a 401 can mean. httpClient calls
 * `clearToken()` only when the *session refresh* returns its own 401/403; if that endpoint 5xx'd
 * or timed out it rethrows the original 401 with the token intact, and a second attempt can
 * still come good. Read at render time, after the interceptor has settled.
 */
export const articleLoadFailureReason = (
  error: unknown,
  signedIn: boolean = isAuthenticated(),
): ArticleLoadFailureReason => {
  const status = articleLoadFailureStatus(error);
  if (status === null) return 'network_error';
  if (status === 404) return 'not_found';
  if (status === 401) return signedIn ? 'auth_refresh_failed' : 'session_expired';
  if (status === 403) return 'forbidden';
  if (status >= 400 && status < 500) return 'bad_request';
  return 'backend_error';
};

const RETRYABLE_REASONS = new Set<ArticleLoadFailureReason>([
  'network_error',
  'backend_error',
  'auth_refresh_failed',
]);

/**
 * Mirrors the rule `shouldRetryQuery` uses to decline *automatic* retries -- the button
 * ignoring it is how a dead control ended up in front of a user, and two sources of truth is
 * how that recurs. `articleLoadFailure.test.ts` asserts they agree across the status range.
 *
 * 401 is the one deliberate exception, and it is not drift: the automatic retry is decided
 * before the interceptor's refresh has resolved, while this button is pressed after, so it can
 * read whether the token survived. `shouldRetryQuery` declining a 401 it cannot yet judge is
 * still correct.
 */
export const isArticleLoadFailureRetryable = (reason: ArticleLoadFailureReason): boolean =>
  RETRYABLE_REASONS.has(reason);
